"use client";

import { Camera, Loader2 } from "lucide-react";
import { motion } from "motion/react";
import { useCallback, useRef, useState } from "react";
import CheckIcon from "@/assets/icons/check.svg";
import { Text } from "@/components/primitives";
import AppImage from "@/components/primitives/Image/Image";
import { Button } from "@/components/ui/button";
import { useAnalytics } from "@/core/analytics";
import { queryClient } from "@/core/caching";
import { useTranslation } from "@/core/i18n";
import { logger } from "@/core/logging";
import { assessmentSessionStorageService } from "@/features/assessments/AssessmentSessionStorageService";
import { useAssessmentStep } from "@/features/assessments/AssessmentStepProvider/AssessmentStepProvider";
import { PhotoPendingState } from "@/features/assessments/components/ImageCaptureStep/stages/photoConfirmationShared";
import type { FrontCaptureResult } from "@/features/assessments/components/ImageCaptureStep/types";
import type { DisplayAgelessAssessment, DisplayFrontalFacialImageLashBrowValidation } from "@/core/api/orval/model";
import { getRetrieveCurrentAssessmentQueryKey } from "@orval/ageless-assessments/ageless-assessments";
import { setAssessmentPhotoCaptureComplete, useRetrieveFrontalFacialImageLashBrowValidation } from "@orval/ageless-imaging/ageless-imaging";
import { POLL_INTERVAL_MS } from "../../assessments.config";
import { getLashBrowValidationIssueTranslationKey } from "../lashBrowValidationIssueTranslations";
import { isRejectedLashBrowValidation } from "../lashBrowValidationIssues";
type LashBrowConfirmStageProps = {
  assessment: DisplayAgelessAssessment;
  headers: HeadersInit;
  frontResult: FrontCaptureResult;
  onConfirmed: () => void;
  onRetake: () => void;
};
export function LashBrowConfirmStage({
  assessment,
  headers,
  frontResult,
  onConfirmed,
  onRetake
}: LashBrowConfirmStageProps): React.ReactElement {
  const {
    t
  } = useTranslation();
  const analytics = useAnalytics();
  const {
    flowType
  } = useAssessmentStep();
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const isConfirmingRef = useRef(false);
  const photoSrc = frontResult.photoData || assessment.frontFaceImageUrl || null;

  // Poll the lash/brow validation verdict for the just-uploaded frontal image. A hard-blocked photo
  // (e.g. eye region hidden by glasses) surfaces the retake prompt here — before capture completes —
  // instead of the user discovering it at the analysis loader. Polling stops once the verdict resolves;
  // an unresolved verdict lets the user proceed, with the analysis step re-checking as a backstop.
  const {
    data: lashBrowValidation
  } = useRetrieveFrontalFacialImageLashBrowValidation(frontResult.uploadedImageId, {
    query: {
      enabled: Boolean(frontResult.uploadedImageId),
      refetchInterval: query => {
        const data = query.state.data as DisplayFrontalFacialImageLashBrowValidation | undefined;
        return data?.isValidated ? false : POLL_INTERVAL_MS;
      }
    },
    request: {
      method: "GET",
      headers
    }
  });
  const isPhotoRejected = isRejectedLashBrowValidation(lashBrowValidation);
  const rejectedIssueKeys = lashBrowValidation?.issueKeys ?? [];
  const trackRetake = useCallback((source: "capture_confirm" | "lash_brow_confirm_rejected") => {
    analytics.trackEvent("assessment_photo_retake_requested", {
      assessmentId: assessment.id,
      flowType,
      photoType: "front",
      source
    });
  }, [analytics, assessment.id, flowType]);
  const handleConfirm = useCallback(async () => {
    if (isConfirmingRef.current) return;
    if (isConfirming) return;
    // Reactive backstop: the CTA is already swapped for a retake button once rejected, but guard the
    // handler too in case the verdict resolves between render and click.
    if (isPhotoRejected) return;
    isConfirmingRef.current = true;
    setIsConfirming(true);
    setConfirmError(null);
    try {
      await setAssessmentPhotoCaptureComplete(assessment.id, {
        frontalFacialImageId: frontResult.uploadedImageId
      }, {
        headers
      });
      await queryClient.invalidateQueries({
        queryKey: getRetrieveCurrentAssessmentQueryKey()
      });

      // The photo passed validation and capture is complete; consume any pending retake request so
      // a later revisit to the capture step starts fresh rather than forcing another retake.
      assessmentSessionStorageService.clearPersistedStudioRetakeRequest(assessment.id);
      analytics.trackEvent("assessment_profile_photos_confirmed", {
        flowType
      });
      onConfirmed();
    } catch (error) {
      logger.error("lash_brow_image_capture_confirm_failed", undefined, error as Error);
      setConfirmError(t("lashBrowSteps.intake.uploadFailed"));
    } finally {
      setIsConfirming(false);
      isConfirmingRef.current = false;
    }
  }, [assessment.id, frontResult, headers, isConfirming, isPhotoRejected, analytics, flowType, onConfirmed, t]);
  return <motion.div animate={{
    opacity: 1
  }} className="inset-0 z-50 flex min-h-dvh flex-col overflow-y-auto bg-neutral-950" exit={{
    opacity: 0
  }} initial={{
    opacity: 0
  }}>
      <div className="pt-safe px-6 pt-8 text-center">
        <Text component="h2" text={t("lashBrowSteps.intake.review.title")} tone="white" variant="heading-xl" />
      </div>

      <div className="flex flex-1 flex-col items-center justify-center px-6 py-8">
        <div className="w-full max-w-68">
          <button type="button" className="group relative aspect-3/4 w-full overflow-hidden rounded-3xl border border-white/15 shadow-2xl shadow-black/60 transition-transform duration-200 active:scale-[0.99]" onClick={() => {
          trackRetake("capture_confirm");
          onRetake();
        }}>
            {photoSrc ? <AppImage fill alt={t("lashBrowSteps.results.heroAlt")} className="object-cover" sizes="(max-width: 640px) 80vw, 272px" src={photoSrc} /> : <PhotoPendingState />}
            <div className="bg-primary absolute top-3 right-3 rounded-full p-2 shadow-lg">
              <CheckIcon className="h-3.5 w-3.5 text-white **:stroke-current" />
            </div>
          </button>
        </div>

        {confirmError && <div className="border-destructive/40 bg-destructive/10 mx-auto mt-6 w-full max-w-68 rounded-xl border p-4">
            <Text component="p" text={confirmError} tone="white" variant="caption" />
          </div>}

        {isPhotoRejected && <div className="border-destructive/40 bg-destructive/10 mx-auto mt-6 w-full max-w-68 space-y-2 rounded-xl border p-4">
            <Text component="p" text={t("lashBrowSteps.analysis.rejected.subtitle")} tone="white" variant="caption" />
            {rejectedIssueKeys.map(key => <Text component="p" key={key} text={t(getLashBrowValidationIssueTranslationKey(key))} tone="white" variant="caption" />)}
          </div>}
      </div>

      <div className="pb-safe mx-auto w-full max-w-md px-6 pb-6">
        <div className="space-y-3">
          {isPhotoRejected ? <Button className="text-foreground h-14 w-full bg-white hover:bg-white/90" onClick={() => {
          trackRetake("lash_brow_confirm_rejected");
          onRetake();
        }}>
              <Camera className="mr-2 h-4 w-4" />
              <Text component="span" text={t("lashBrowSteps.analysis.rejected.retakeCta")} />
            </Button> : <Button className="text-foreground h-14 w-full bg-white hover:bg-white/90" disabled={isConfirming} onClick={handleConfirm}>
              {isConfirming ? <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  <Text component="span" text={t("assessmentSteps.studioSession.profiles.reviewConfirming")} />
                </> : <>
                  <CheckIcon className="mr-2 h-4 w-4" />
                  <Text component="span" text={t("assessmentSteps.studioSession.preview.continue")} />
                </>}
            </Button>}
          <button className="type-caption mx-auto flex items-center justify-center gap-1.5 pt-1 text-white/70 transition-colors hover:text-white focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:ring-offset-2 focus-visible:ring-offset-black focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50" disabled={isConfirming} type="button" onClick={() => {
          trackRetake("capture_confirm");
          onRetake();
        }}>
            <Camera className="h-3.5 w-3.5 stroke-[1.5]" />
            <Text component="span" text={t("assessmentSteps.studioSession.profiles.retakeAll")} variant="caption" />
          </button>
        </div>
      </div>
    </motion.div>;
}